import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API from "../../utils/interceptor";
import { getQueryString } from "../../utils/helper";
import { setToken, getToken } from "../../utils/tokenStore";

const initialState = {
  loading: false,
  isAuthorized: false,
  message: "",
  userRoles: [] as string[],
};

/** Extract access_token from the URL hash and store it in memory. */
const extractTokenFromHash = (): string | null => {
  const hash = window.location.hash.substring(1);
  const params = new URLSearchParams(hash);
  const token = params.get("access_token");
  if (token) {
    setToken(token);
    // Clean the hash from the URL so the token is not visible in the address bar
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + window.location.search
    );
  }
  return token;
};

export const getAuth = createAsyncThunk(
  "auth/getAuth",
  async (_, { getState, rejectWithValue }) => {
    try {
      // 1. Try to get the token from the URL hash (new OAuth redirect flow)
      extractTokenFromHash();

      // 2. Read URL query params — Docebo passes these in the iframe URL
      const url = window.location.href;
      const params: any = new URL(url).searchParams;
      const queryParameters: any = {};
      for (const [key, value] of params.entries()) {
        queryParameters[key] = value;
      }

      const userId = parseInt(queryParameters.user_id);
      const username = encodeURIComponent(queryParameters.username || "");
      const doceboHash = queryParameters.hash;
      const authCode = queryParameters.auth_code;
      const courseId = queryParameters.course_id;
      const courseCode = queryParameters.course_code;

      if (!isNaN(userId)) {
        sessionStorage.setItem("userId", userId.toString());
      }
      if (username) {
        sessionStorage.setItem("username", username);
      }

      const body = {
        user_id: isNaN(userId) ? undefined : userId,
        username: username || undefined,
        docebo_hash: doceboHash || undefined,
        auth_code: authCode || undefined,
        course_id: courseId || undefined,
        course_code: courseCode || undefined,
      };

      // 3. Always call /authorization — the Bearer header (if a hash token was found)
      //    is injected by the interceptor. The backend may also return an access_token
      //    in the response body which we store for subsequent calls.
      const response = await API.post("authorization", body);

      if (response.status !== 200) {
        throw rejectWithValue("Authorization failed");
      }

      // 4. If the backend returns an access_token in the response, store it.
      //    This covers the case where the backend exchanges Docebo params for a token.
      if (response.data?.access_token) {
        setToken(response.data.access_token);
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

export const getUserRoles = createAsyncThunk(
  "auth/getUserRoles",
  async (_, { getState, rejectWithValue }) => {
    try {
      const query = getQueryString({
        user_id: sessionStorage.getItem("userId"),
      });

      const url = `${process.env.REACT_APP_BASE_API_URL}/users${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((res) => res.json());

      sessionStorage.setItem("first_name", response.data.first_name);
      sessionStorage.setItem("last_name", response.data.last_name);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAuth.pending, (state) => {
        state.loading = true;
        state.message = "";
      })
      .addCase(getAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthorized = true;
        state.message =
          (action.payload.message as string) || "Authorization successful";
      })
      .addCase(getAuth.rejected, (state, action) => {
        state.loading = false;
        state.isAuthorized = false;
        state.message = (action.payload as string) || "Authorization failed";
      })
      .addCase(getUserRoles.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserRoles.fulfilled, (state, { payload }) => {
        state.userRoles = payload?.roles;
        state.loading = false;
        if (payload.username)
          sessionStorage.setItem("username", payload?.username?.toString());
      })
      .addCase(getUserRoles.rejected, (state) => {
        state.loading = false;
      });
  },
});

export default authSlice.reducer;
